/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import Button, { ButtonPresentationProps, ButtonVariants } from "@stoneage/MobiusOverrides/Button/Button";
import LazyImage from "@stoneage/MobiusOverrides/LazyImage/LazyImage";
import { LinkPresentationProps } from "@stoneage/MobiusOverrides/Link";
import { xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    dimensions = "dimensions",
    title = "title",
    titleStyle = "titleStyle",
    imageUrl = "imageUrl",
    copy = "copy",
    buttonText = "buttonText",
    buttonStyle = "buttonStyle",
    buttonId = "buttonId",
    url = "url",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.dimensions]: string;
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.imageUrl]: string;
        [fields.copy]: string;
        [fields.buttonText]: string;
        [fields.buttonStyle]: ButtonVariants;
        [fields.buttonId]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {};
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductFamilyCardStyles {
    // as button pres props
    widgetContainerAsButton?: InjectableCss;
    dimensionsAsButton?: TypographyPresentationProps;
    imageContainerAsButton?: InjectableCss;
    dimensionsWrapperAsButton?: InjectableCss;
    firstWrapper?: InjectableCss;
    secondWrapper?: InjectableCss;
    copyAsButton?: TypographyPresentationProps;
    titleAsButton?: TypographyPresentationProps;
    button?: ButtonPresentationProps;

    // as link pres props
    widgetContainer?: InjectableCss;
    dimensions?: TypographyPresentationProps;
    imageContainer?: InjectableCss;
    dimensionsWrapper?: InjectableCss;
    copy?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    accordion?: AccordionPresentationProps;
}

export const productFamilyCardStyles: ProductFamilyCardStyles = {
    // as button styles
    widgetContainerAsButton: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 17px;
            border: 1px solid ${getColor("common.ui5")};
            align-items: center;
            height: 100%;
            justify-content: space-between;

            ${xScreen("lte", "md")} {
                flex-direction: row;
                align-items: center;
                justify-content: center;
            }
        `,
    },
    dimensionsAsButton: {
        size: 20,
        lineHeight: "24px",
        weight: "bold",
        color: "common.black",
    },
    dimensionsWrapperAsButton: {
        css: css`
            text-align: center;
        `,
    },
    imageContainerAsButton: {
        css: css`
            width: 100%;
            height: 121px;
            display: flex;
            overflow: hidden;
            align-items: center;
            justify-content: center;
            margin-bottom: 12px;
            margin-top: auto;
            img {
                max-height: 121px !important;
            }

            ${xScreen("lte", "md")} {
                margin-bottom: 0;
                &,
                img {
                    max-width: 137px !important;
                    max-height: 79px !important;
                }
            }
        `,
    },
    secondWrapper: {
        css: css`
            margin-top: auto;

            ${xScreen("lte", "md")} {
                margin-top: 0;
                padding-left: 8px;
            }
        `,
    },
    firstWrapper: {
        css: css`
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;

            ${xScreen("lte", "md")} {
                padding-right: 8px;
                max-width: 145px;
            }
        `,
    },
    titleAsButton: {
        weight: "bold",
        color: "common.ui12",
        css: css`
            text-align: center;
            margin-bottom: 16px;
            margin-top: auto;
        `,
    },
    copyAsButton: {
        color: "text.main",
        size: 15,
        lineHeight: "26px",
        css: css`
            margin-bottom: 12px;
        `,
    },
    button: {
        css: css`
            width: fit-content;
            margin-top: auto;
            padding-left: 12px;
            padding-right: 12px;
            * {
                font-size: 12px;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 14px;
            }
        `,
    },

    // as link styles
    widgetContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
        `,
    },
    dimensions: {
        size: 24,
        lineHeight: "26px",
        weight: "bold",
        color: "common.white",
        css: css`
            letter-spacing: 3px;
        `,
    },
    dimensionsWrapper: {
        css: css`
            padding: 8px;
            width: 100%;
            background-color: ${getColor("common.ui7")};
            margin-bottom: 24px;
            text-align: center;
        `,
    },
    link: {
        css: css`
            > ${TypographyStyle} {
                pointer-events: none;
            }
            &:hover,
            &:focus {
                .dimensions-wrapper {
                    background-color: ${getColor("primary")};
                }
            }
        `,
    },
    imageContainer: {
        css: css`
            width: 100%;
            height: 121px;
            margin-bottom: 16px;
            display: flex;
            overflow: hidden;
            align-items: center;
            justify-content: center;
            img {
                max-height: 121px !important;
            }
        `,
    },
    title: {
        weight: "bold",
        color: "common.ui12",
        css: css`
            text-align: center;
            margin-bottom: 12px;
        `,
    },
    copy: {
        color: "text.main",
        size: 16,
        lineHeight: "26px",
    },
    accordion: {
        css: css`
            width: 100%;
            dt {
                button {
                    background-color: ${getColor("common.ui7")};
                    border-color: ${getColor("common.ui7")};
                    span {
                        font-size: 24px;
                        font-weight: bold;
                        letter-spacing: 0;
                        line-height: 26px;
                        color: ${getColor("common.white")};
                    }

                    span[class*="IconWrapper"] {
                        &,
                        svg {
                            width: 19px;
                            height: 19px;
                            color: ${getColor("common.white")};
                        }
                    }
                }
            }

            && dd {
                background: transparent;
            }
        `,
    },
};

const styles = productFamilyCardStyles;

const ProductFamilyCard = ({ id, fields }: Props) => {
    const { dimensions, title, titleStyle, imageUrl, copy, buttonText, buttonStyle, buttonId, url, openInNewTab } = fields;

    const linkUrl = useGetLink(url);
    const copyText = parse(copy, parserOptions);
    const [renderAsButton, setRenderAsButton] = React.useState(false);
    React.useEffect(() => {
        if (fields?.buttonText?.length) {
            setRenderAsButton(true);
        } else {
            setRenderAsButton(false);
        }
    }, [fields?.buttonText]);

    const productAsLinkContentElement = (
        <>
            {imageUrl?.length > 0 && (
                <StyledWrapper {...styles.imageContainer}>
                    <LazyImage
                        src={imageUrl}
                        altText={imageUrl}
                        isCatalogImage={false}
                        height="160"
                        cloudinaryWidth="418"
                    />
                </StyledWrapper>
            )}
            {title?.length > 0 && (
                <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography}>
                    {parse(title, parserOptions)}
                </Typography>
            )}
            {copyText && copy.length > 0 && (
                <Typography as="div" {...styles.copy}>
                    {copyText}
                </Typography>
            )}
        </>
    );

    return (
        <StyledWrapper
            css={css`
                height: 100%;
            `}
        >
            {renderAsButton ? (
                <StyledWrapper {...styles.widgetContainerAsButton}>
                    <StyledWrapper {...styles.firstWrapper}>
                        {dimensions && (
                            <StyledWrapper {...styles.dimensionsWrapperAsButton} className="dimensions-wrapper">
                                <Typography {...styles.dimensionsAsButton}>{dimensions}</Typography>
                            </StyledWrapper>
                        )}
                        {copyText && copy.length > 0 && (
                            <Typography as="div" {...styles.copyAsButton}>
                                {copyText}
                            </Typography>
                        )}
                        {imageUrl?.length > 0 && (
                            <StyledWrapper {...styles.imageContainerAsButton}>
                                <LazyImage
                                    src={imageUrl}
                                    altText={imageUrl}
                                    isCatalogImage={false}
                                    height="160"
                                    cloudinaryWidth="418"
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.secondWrapper}>
                        {title?.length > 0 && (
                            <Typography {...styles.titleAsButton} variant={titleStyle as keyof ThemeTypography}>
                                {parse(title, parserOptions)}
                            </Typography>
                        )}
                        <Button
                            variant={buttonStyle}
                            href={linkUrl?.url}
                            showIcon={false}
                            target={openInNewTab ? "_blank" : ""}
                            {...styles.button}
                            {...buttonId && { id: `${buttonId}_${id}` }}
                        >
                            {buttonText || linkUrl?.title}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <>
                    <Hidden below="sm">
                        <StyledWrapper {...styles.widgetContainer}>
                            <Link href={linkUrl?.url} target={openInNewTab ? "_blank" : ""} {...buttonId && { id: `${buttonId}_${id}` }} {...styles.link}>
                                {dimensions && (
                                    <StyledWrapper {...styles.dimensionsWrapper} className="dimensions-wrapper">
                                        <Typography {...styles.dimensions}>{dimensions}</Typography>
                                    </StyledWrapper>
                                )}
                                {productAsLinkContentElement}
                            </Link>
                        </StyledWrapper>
                    </Hidden>
                    <Hidden above="xs">
                        <Accordion headingLevel={0} {...styles.accordion}>
                            <AccordionSection title={dimensions}>{productAsLinkContentElement}</AccordionSection>
                        </Accordion>
                    </Hidden>
                </>
            )}
        </StyledWrapper>
    );
};

const productFamilyCard: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductFamilyCard),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.dimensions,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.imageUrl,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.copy,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.buttonText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.buttonStyle,
                editorTemplate: "DropDownField",
                defaultValue: "primary",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
            },
            {
                name: fields.buttonId,
                displayName: "Button Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default productFamilyCard;
